// Buscador tipo typeahead: consulta el servidor mientras el usuario escribe
// (debounced) y muestra un dropdown de resultados. Para selects con mucho volumen
// (cuentas, contactos) donde un <select> con miles de opciones no sirve.
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.ts";
import type { Row } from "../lib/resource.ts";

export function AsyncCombobox(
  { path, params, labelOf, value, selectedLabel, onChange, placeholder, disabled }:
  {
    path: string;
    params?: Record<string, string>;
    labelOf: (r: Row) => string;
    value: string;
    selectedLabel?: string;
    onChange: (id: string, row: Row | null) => void;
    placeholder?: string;
    disabled?: boolean;
  },
): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [label, setLabel] = useState(selectedLabel ?? "");
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => { const t = setTimeout(() => setDebounced(query.trim()), 300); return () => clearTimeout(t); }, [query]);
  useEffect(() => { if (selectedLabel !== undefined) setLabel(selectedLabel); }, [selectedLabel]);
  useEffect(() => {
    const onDoc = (e: MouseEvent): void => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const qs = new URLSearchParams({ pageSize: "20", ...(debounced ? { q: debounced } : {}), ...(params ?? {}) }).toString();
  const results = useQuery<{ rows: Row[] }>({
    queryKey: ["combobox", path, params ?? {}, debounced],
    queryFn: () => api.get(`/${path}?${qs}`),
    enabled: open && !disabled,
    staleTime: 30_000,
  });

  const pick = (r: Row): void => { onChange(String(r["id"]), r); setLabel(labelOf(r)); setQuery(""); setOpen(false); };
  const clear = (): void => { onChange("", null); setLabel(""); setQuery(""); };
  const rows = results.data?.rows ?? [];

  return (
    <div className="relative" ref={boxRef}>
      {value && !open ? (
        <div className={`input flex items-center justify-between ${disabled ? "" : "cursor-text"}`} onClick={() => !disabled && setOpen(true)}>
          <span className="truncate">{label || "…"}</span>
          {!disabled && <button type="button" className="text-ink-muted hover:text-ink-secondary ml-2 shrink-0" onClick={(e) => { e.stopPropagation(); clear(); }} aria-label="Limpiar">✕</button>}
        </div>
      ) : (
        <div className="relative">
          <input
            className="input w-full pr-8"
            placeholder={placeholder ?? "Buscar…"}
            value={query}
            disabled={disabled}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          />
          {query && (
            <button
              type="button"
              aria-label="Limpiar"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary text-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setQuery("")}
            >✕</button>
          )}
        </div>
      )}
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full card max-h-64 overflow-y-auto shadow-lg">
          {results.isLoading ? (
            <div className="px-3 py-2 text-sm text-ink-muted">Buscando…</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ink-muted">{debounced ? "Sin resultados" : "Escribe para buscar…"}</div>
          ) : (
            rows.map((r) => (
              <button
                type="button"
                key={String(r["id"])}
                className="w-full text-left px-3 py-2 text-sm hover:bg-surface-hover truncate"
                onClick={() => pick(r)}
              >
                {labelOf(r)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
